import React, {
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Grid,
  Stack,
  Tooltip,
  Chip,
  Divider,
  Autocomplete,
  Tabs,
  Tab,
  CircularProgress,
  Snackbar,
  Alert,
  LinearProgress,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import ListAltIcon from "@mui/icons-material/ListAlt";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import DescriptionIcon from "@mui/icons-material/Description";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import ZoomOutMapIcon from "@mui/icons-material/ZoomOutMap";
import DownloadIcon from "@mui/icons-material/Download";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ImageIcon from "@mui/icons-material/Image";
import RotateRightIcon from "@mui/icons-material/RotateRight";
import CloseIcon from "@mui/icons-material/Close";
import { AutoTaskAuthContext } from "../Context/AutoTaskContext";
import { isDeptHead, isHead } from "../utils/roles";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import * as XLSX from "xlsx";
import ExcelIcon from "../assets/icons/excel_export.png";

// ─── Inline DocumentViewerDialog (same as the shared component) ───────────────

const DOC_VIEWER_PRIMARY = "#7B6ED6";

const getFileType = (file, fileName) => {
  const src = file || fileName || "";
  const ext = src.split("?")[0].split(".").pop().toLowerCase();
  if (["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg"].includes(ext))
    return "image";
  if (ext === "pdf") return "pdf";
  return "other";
};

const isBase64 = (str) => typeof str === "string" && str.startsWith("data:");

const getDisplayUrl = (file) => {
  if (!file || file === "No file" || file === "null" || file === "undefined")
    return null;
  // Must be a base64 data URL or a proper http/https URL — otherwise treat as no file
  if (
    typeof file === "string" &&
    (file.startsWith("data:") ||
      file.startsWith("http://") ||
      file.startsWith("https://"))
  ) {
    return file;
  }
  return null;
};

const formatViewerDate = (dateStr) => {
  if (!dateStr) return null;
  const parts = dateStr.split("-");
  if (parts.length === 3 && parts[0].length === 4)
    return `${parts[2]}-${parts[1]}-${parts[0]}`;
  return dateStr;
};

const ImageViewer = ({ src, alt }) => {
  const [scale, setScale] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const dragStart = useRef(null);
  const containerRef = useRef(null);
  const clampScale = (s) => Math.min(Math.max(s, 0.5), 6);
  const zoom = (delta) => setScale((prev) => clampScale(prev + delta));
  const reset = () => {
    setScale(1);
    setOffset({ x: 0, y: 0 });
    setRotation(0);
  };
  const rotate = () => setRotation((r) => (r + 90) % 360);
  const handleWheel = useCallback((e) => {
    e.preventDefault();
    setScale((prev) => clampScale(prev + (e.deltaY < 0 ? 0.15 : -0.15)));
  }, []);
  useEffect(() => {
    const el = containerRef.current;
    if (el) el.addEventListener("wheel", handleWheel, { passive: false });
    return () => {
      if (el) el.removeEventListener("wheel", handleWheel);
    };
  }, [handleWheel]);
  const onMouseDown = (e) => {
    if (scale <= 1) return;
    setIsDragging(true);
    dragStart.current = { x: e.clientX - offset.x, y: e.clientY - offset.y };
  };
  const onMouseMove = (e) => {
    if (!isDragging || !dragStart.current) return;
    setOffset({
      x: e.clientX - dragStart.current.x,
      y: e.clientY - dragStart.current.y,
    });
  };
  const onMouseUp = () => {
    setIsDragging(false);
    dragStart.current = null;
  };
  const lastTouchDist = useRef(null);
  const onTouchStart = (e) => {
    if (e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      lastTouchDist.current = Math.sqrt(dx * dx + dy * dy);
    }
  };
  const onTouchMove = (e) => {
    if (e.touches.length === 2 && lastTouchDist.current !== null) {
      e.preventDefault();
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      setScale((prev) =>
        clampScale(prev + (dist - lastTouchDist.current) * 0.01),
      );
      lastTouchDist.current = dist;
    }
  };
  const onTouchEnd = () => {
    lastTouchDist.current = null;
  };
  if (error)
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: 300,
          gap: 1,
          color: "text.secondary",
        }}
      >
        <ImageIcon sx={{ fontSize: 48, opacity: 0.4 }} />
        <Typography variant="body2">Unable to load image</Typography>
      </Box>
    );
  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          px: 1,
          py: 0.75,
          borderBottom: "1px solid",
          borderColor: "divider",
          backgroundColor: "#fafafa",
        }}
      >
        <Tooltip title="Zoom out">
          <IconButton
            size="small"
            onClick={() => zoom(-0.25)}
            disabled={scale <= 0.5}
          >
            <ZoomOutIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Box
          sx={{
            minWidth: 52,
            textAlign: "center",
            fontSize: "0.78rem",
            fontWeight: 600,
            color: DOC_VIEWER_PRIMARY,
            userSelect: "none",
          }}
        >
          {Math.round(scale * 100)}%
        </Box>
        <Tooltip title="Zoom in">
          <IconButton
            size="small"
            onClick={() => zoom(0.25)}
            disabled={scale >= 6}
          >
            <ZoomInIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        <Tooltip title="Rotate 90°">
          <IconButton size="small" onClick={rotate}>
            <RotateRightIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Reset view">
          <IconButton size="small" onClick={reset}>
            <ZoomOutMapIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" sx={{ color: "text.disabled", mr: 1 }}>
          Scroll or pinch to zoom · Drag to pan
        </Typography>
      </Box>
      <Box
        ref={containerRef}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        sx={{
          width: "100%",
          height: { xs: 320, sm: 480 },
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#1a1a2e",
          cursor: scale > 1 ? (isDragging ? "grabbing" : "grab") : "default",
          position: "relative",
          userSelect: "none",
        }}
      >
        {!loaded && (
          <Box sx={{ position: "absolute", top: 0, left: 0, right: 0 }}>
            <LinearProgress
              sx={{
                "& .MuiLinearProgress-bar": {
                  backgroundColor: DOC_VIEWER_PRIMARY,
                },
              }}
            />
          </Box>
        )}
        <img
          src={src}
          alt={alt}
          draggable={false}
          onLoad={() => setLoaded(true)}
          onError={() => setError(true)}
          style={{
            maxWidth: "100%",
            maxHeight: "100%",
            objectFit: "contain",
            transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale}) rotate(${rotation}deg)`,
            transition: isDragging ? "none" : "transform 0.15s ease",
            display: loaded ? "block" : "none",
            willChange: "transform",
          }}
        />
        {!loaded && !error && (
          <Box sx={{ color: "rgba(255,255,255,0.3)", fontSize: "0.85rem" }}>
            Loading…
          </Box>
        )}
      </Box>
    </Box>
  );
};

const PdfViewer = ({ src, fileName }) => {
  const [pdfFailed, setPdfFailed] = useState(false);
  const [objectUrl, setObjectUrl] = useState(null);
  useEffect(() => {
    if (isBase64(src) && src.startsWith("data:application/pdf")) {
      const byteString = atob(src.split(",")[1]);
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++)
        ia[i] = byteString.charCodeAt(i);
      const blob = new Blob([ab], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      setObjectUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    return undefined;
  }, [src]);
  const displaySrc = objectUrl || src;
  const handleDownload = () => {
    const a = document.createElement("a");
    a.href = displaySrc;
    a.download = fileName || "document.pdf";
    a.click();
  };
  if (pdfFailed)
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: 300,
          gap: 2,
          color: "text.secondary",
          p: 3,
        }}
      >
        <PictureAsPdfIcon
          sx={{ fontSize: 56, color: "#e74c3c", opacity: 0.7 }}
        />
        <Typography variant="body2" align="center">
          Your browser couldn't display this PDF inline.
        </Typography>
        <Box
          sx={{
            display: "flex",
            gap: 1,
            flexWrap: "wrap",
            justifyContent: "center",
          }}
        >
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleDownload}
            sx={{ backgroundColor: DOC_VIEWER_PRIMARY, textTransform: "none" }}
          >
            Download PDF
          </Button>
          <Button
            variant="outlined"
            startIcon={<OpenInNewIcon />}
            onClick={() => window.open(displaySrc, "_blank")}
            sx={{ textTransform: "none" }}
          >
            Open in new tab
          </Button>
        </Box>
      </Box>
    );
  return (
    <Box sx={{ position: "relative" }}>
      <iframe
        src={displaySrc}
        title={fileName || "PDF Document"}
        width="100%"
        style={{
          height: "min(70vh, 600px)",
          border: "none",
          display: "block",
          backgroundColor: "#525659",
        }}
        onError={() => setPdfFailed(true)}
      />
      <Box
        sx={{
          position: "absolute",
          top: 8,
          right: 8,
          display: "flex",
          gap: 0.5,
        }}
      >
        <Tooltip title="Download">
          <IconButton
            size="small"
            onClick={handleDownload}
            sx={{
              backgroundColor: "rgba(0,0,0,0.55)",
              color: "#fff",
              "&:hover": { backgroundColor: "rgba(0,0,0,0.75)" },
            }}
          >
            <DownloadIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Open in new tab">
          <IconButton
            size="small"
            onClick={() => window.open(displaySrc, "_blank")}
            sx={{
              backgroundColor: "rgba(0,0,0,0.55)",
              color: "#fff",
              "&:hover": { backgroundColor: "rgba(0,0,0,0.75)" },
            }}
          >
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

const OtherFileViewer = ({ src, fileName }) => {
  const handleDownload = () => {
    const a = document.createElement("a");
    a.href = src;
    a.download = fileName || "document";
    a.target = "_blank";
    a.click();
  };
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: 260,
        gap: 2,
        color: "text.secondary",
        p: 3,
      }}
    >
      <InsertDriveFileIcon sx={{ fontSize: 64, opacity: 0.35 }} />
      <Typography variant="body2" align="center">
        Preview is not available for this file type.
      </Typography>
      <Box
        sx={{
          display: "flex",
          gap: 1,
          flexWrap: "wrap",
          justifyContent: "center",
        }}
      >
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={handleDownload}
          sx={{ backgroundColor: DOC_VIEWER_PRIMARY, textTransform: "none" }}
        >
          Download File
        </Button>
        <Button
          variant="outlined"
          startIcon={<OpenInNewIcon />}
          onClick={() => window.open(src, "_blank")}
          sx={{ textTransform: "none" }}
        >
          Open in new tab
        </Button>
      </Box>
    </Box>
  );
};

const NoFileView = () => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      height: 220,
      gap: 1.5,
      color: "text.secondary",
    }}
  >
    <InsertDriveFileIcon sx={{ fontSize: 56, opacity: 0.25 }} />
    <Typography variant="body2">No file attached to this document</Typography>
  </Box>
);

const DocumentViewerDialog = ({ doc, onClose }) => {
  if (!doc) return null;
  const fileUrl = getDisplayUrl(doc.file);
  const fileType = fileUrl ? getFileType(doc.file, doc.fileName) : "none";
  const typeLabel = {
    image: "Image",
    pdf: "PDF",
    other: "File",
    none: "No file",
  }[fileType];
  const typeIcon = {
    image: <ImageIcon sx={{ fontSize: 15 }} />,
    pdf: <PictureAsPdfIcon sx={{ fontSize: 15 }} />,
    other: <InsertDriveFileIcon sx={{ fontSize: 15 }} />,
    none: <InsertDriveFileIcon sx={{ fontSize: 15 }} />,
  }[fileType];
  const typeColor = {
    image: { bg: "#e8f5e9", color: "#2e7d32" },
    pdf: { bg: "#fce4ec", color: "#c62828" },
    other: { bg: "#e3f2fd", color: "#1565c0" },
    none: { bg: "#f5f5f5", color: "#757575" },
  }[fileType];
  const handleDownload = () => {
    if (!fileUrl) return;
    const a = document.createElement("a");
    a.href = fileUrl;
    a.download = doc.fileName || `${doc.name}_${doc.number}`;
    a.target = "_blank";
    a.click();
  };
  return (
    <Dialog
      open={!!doc}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          boxShadow: "0 24px 64px rgba(0,0,0,0.22)",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 1,
          pb: 1,
          borderBottom: "1px solid",
          borderColor: "divider",
          pr: 1,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Typography
              variant="subtitle1"
              fontWeight={700}
              sx={{
                color: DOC_VIEWER_PRIMARY,
                lineHeight: 1.3,
                wordBreak: "break-word",
              }}
            >
              {doc.name}
            </Typography>
            <Chip
              icon={typeIcon}
              label={typeLabel}
              size="small"
              sx={{
                height: 20,
                fontSize: "0.68rem",
                fontWeight: 600,
                backgroundColor: typeColor.bg,
                color: typeColor.color,
                "& .MuiChip-icon": { color: typeColor.color, ml: "6px" },
              }}
            />
          </Box>
          <Box sx={{ display: "flex", gap: 2, mt: 0.5, flexWrap: "wrap" }}>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              <strong>Number:</strong> {doc.number}
            </Typography>
            {doc.renewalDate && (
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                <strong>Renewal:</strong> {formatViewerDate(doc.renewalDate)}
              </Typography>
            )}
            {doc.fileName && doc.fileName !== "null" && (
              <Typography
                variant="caption"
                sx={{
                  color: "text.disabled",
                  maxWidth: 260,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  display: "block",
                }}
              >
                {doc.fileName}
              </Typography>
            )}
          </Box>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ mt: 0.25 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        {fileType === "none" && <NoFileView />}
        {fileType === "image" && <ImageViewer src={fileUrl} alt={doc.name} />}
        {fileType === "pdf" && (
          <PdfViewer src={fileUrl} fileName={doc.fileName || doc.name} />
        )}
        {fileType === "other" && (
          <OtherFileViewer src={fileUrl} fileName={doc.fileName || doc.name} />
        )}
      </DialogContent>
      <DialogActions
        sx={{
          px: 2,
          py: 1.25,
          borderTop: "1px solid",
          borderColor: "divider",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="caption" sx={{ color: "text.disabled" }}>
          {fileType === "image"
            ? "Use scroll wheel or pinch to zoom. Drag to pan when zoomed."
            : fileType === "pdf"
            ? "PDF rendered inline. Use toolbar controls inside viewer."
            : ""}
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          {fileUrl && fileType !== "none" && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
              sx={{
                textTransform: "none",
                borderColor: DOC_VIEWER_PRIMARY,
                color: DOC_VIEWER_PRIMARY,
              }}
            >
              Download
            </Button>
          )}
          <Button
            size="small"
            variant="contained"
            onClick={onClose}
            sx={{ textTransform: "none", backgroundColor: DOC_VIEWER_PRIMARY }}
          >
            Close
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:5001";

const ReminderMaster = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const contextData = useContext(AutoTaskAuthContext);
  const { allUsers } = contextData;
  const userInfo = contextData?.userInfo;
  const userStoreID = userInfo?.store_id;
  const user_id = userInfo?.id;
  const allStaff = allUsers;

  const isHeadRole =
    isHead(userInfo.role) || isDeptHead(userInfo.role);

  const initialFormState = {
    type: "",
    title: "",
    description: "",
    reminder_datetime: "",
    status: "UPCOMING",
    repeat_type: "NONE",
    assignee_id: null,
    document_id: null,
  };

  const [formData, setFormData] = useState(initialFormState);
  const [reminders, setReminders] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [documentStats, setDocumentStats] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [selectedReminderId, setSelectedReminderId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [reminderToDelete, setReminderToDelete] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  // Document viewer state
  const [viewerDoc, setViewerDoc] = useState(null);

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Filter states for Reminders
  const [filters, setFilters] = useState({
    assigneeName: "",
    titleSearch: "",
    status: "",
    dateFrom: "",
    dateTo: "",
  });

  // Filter states for Documents
  const [documentFilters, setDocumentFilters] = useState({
    code: "",
    status: "",
    dateFrom: "",
    dateTo: "",
  });

  // Helper function to format date as dd-mm-yyyy
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Show snackbar helper
  const showSnackbar = (message, severity = "success") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  // Close snackbar
  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  // Fetch all reminders on component mount
  useEffect(() => {
    fetchReminders();
    if (tabValue === 1) {
      fetchDocuments();
    }
  }, [userStoreID, tabValue]);

  // Re-fetch documents when returning from a master page after editing
  useEffect(() => {
    if (location.state?.fromMasterEdit) {
      fetchDocuments();
      // Clear the state so it doesn't re-trigger on further navigations
      window.history.replaceState({}, "");
    }
  }, [location.state]);

  // Navigate to Vehicle or Driver master with the owner id pre-loaded
  const handleNavigateToMaster = (doc) => {
    const masterType = doc.doc_master_type; // "V" for vehicle, "D" for driver
    const ownerId = doc.owner?.owner_id;

    if (masterType === "V") {
      navigate("/travel/vehicle-master", {
        state: {
          store_id: userStoreID,
          preloadVehicleId: ownerId,
          returnTo: "/reminders",
        },
      });
    } else if (masterType === "D") {
      navigate("/travel/driver-master", {
        state: {
          store_id: userStoreID,
          preloadDriverId: ownerId,
          returnTo: "/reminders",
        },
      });
    }
  };

  const fetchReminders = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_BASE_URL}/api/reminders/store/${userStoreID}`,
      );
      if (response.data.success) {
        setReminders(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching reminders:", error);
      showSnackbar("Failed to fetch reminders", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_BASE_URL}/api/reminders/document-expiry/${userStoreID}`,
      );
      if (response.data.success) {
        setDocuments(response.data.data);
        setDocumentStats(response.data.summary);
      }
    } catch (error) {
      console.error("Error fetching documents:", error);
      showSnackbar("Failed to fetch documents", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleUserChange = (event, newValue) => {
    setFormData({
      ...formData,
      assignee_id: newValue ? newValue.id : null,
    });
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({
      ...filters,
      [name]: value,
    });
  };

  const handleDocumentFilterChange = (e) => {
    const { name, value } = e.target;
    setDocumentFilters({
      ...documentFilters,
      [name]: value,
    });
  };

  const handleSaveReminder = async () => {
    if (!formData.type || !formData.title || !formData.reminder_datetime) {
      showSnackbar(
        "Please fill in all required fields (Type, Title, Date & Time)",
        "warning",
      );
      return;
    }

    // Build payload based on reminder type
    let payload = {
      store_id: userStoreID,
      type: formData.type.toUpperCase(),
      title: formData.title,
      description: formData.description,
      reminder_datetime: formData.reminder_datetime,
      repeat_type: formData.repeat_type,
      created_by: user_id,
    };

    // Add assignee_id if present (for USER type)
    if (formData.assignee_id) {
      payload.assignee_id = formData.assignee_id;
    }

    // Add document_id if present (for DOCUMENT type)
    if (formData.document_id) {
      payload.document_id = formData.document_id;
    }

    try {
      setLoading(true);
      if (editMode) {
        // Update reminder - PUT /api/reminders/:id
        const updatePayload = {
          store_id: userStoreID,
          title: formData.title,
          description: formData.description,
          reminder_datetime: formData.reminder_datetime,
          repeat_type: formData.repeat_type,
          status: formData.status,
        };

        if (formData.assignee_id) {
          updatePayload.assignee_id = formData.assignee_id;
        }

        console.log("Updating Reminder:", updatePayload);
        const response = await axios.put(
          `${API_BASE_URL}/api/reminders/${selectedReminderId}`,
          updatePayload,
        );

        if (response.data.success) {
          showSnackbar("Reminder updated successfully", "success");
          fetchReminders();
          handleClearForm();
        }
      } else {
        // Create reminder - POST /api/reminders/
        console.log("Creating Reminder:", payload);
        const response = await axios.post(
          `${API_BASE_URL}/api/reminders`,
          payload,
        );

        if (response.data.success) {
          showSnackbar("Reminder created successfully", "success");
          fetchReminders();
          handleClearForm();
        }
      }
    } catch (error) {
      console.error("Error saving reminder:", error);
      showSnackbar(
        error.response?.data?.error || "Failed to save reminder",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClearForm = () => {
    setFormData(initialFormState);
    setEditMode(false);
    setSelectedReminderId(null);
  };

  const handleEditReminder = (reminder) => {
    setFormData({
      type: reminder.type,
      title: reminder.title,
      description: reminder.description || "",
      reminder_datetime: reminder.reminder_datetime
        ? new Date(reminder.reminder_datetime).toISOString().slice(0, 16)
        : "",
      status: reminder.status,
      repeat_type: reminder.repeat_type,
      assignee_id: reminder.assignee_id,
      document_id: reminder.document_id,
    });
    setEditMode(true);
    setSelectedReminderId(reminder.reminder_id);
  };

  const handleDeleteClick = (reminder) => {
    setReminderToDelete(reminder);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      setLoading(true);
      console.log("Deleting Reminder:", reminderToDelete.reminder_id);
      const response = await axios.delete(
        `${API_BASE_URL}/api/reminders/${userStoreID}/${reminderToDelete.reminder_id}`,
      );

      if (response.data.success) {
        showSnackbar("Reminder deleted successfully", "success");
        fetchReminders();
      }
    } catch (error) {
      console.error("Error deleting reminder:", error);
      showSnackbar("Failed to delete reminder", "error");
    } finally {
      setLoading(false);
      setDeleteDialogOpen(false);
      setReminderToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setReminderToDelete(null);
  };

  const handleStatusChange = async (reminderId, newStatus) => {
    try {
      setLoading(true);
      const response = await axios.patch(
        `${API_BASE_URL}/${reminderId}/api/reminders/status`,
        {
          store_id: userStoreID,
          status: newStatus,
        },
      );

      if (response.data.success) {
        showSnackbar("Status updated successfully", "success");
        fetchReminders();
      }
    } catch (error) {
      console.error("Error updating status:", error);
      showSnackbar("Failed to update status", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleClearFilters = () => {
    setFilters({
      assigneeName: "",
      titleSearch: "",
      status: "",
      dateFrom: "",
      dateTo: "",
    });
  };

  const handleClearDocumentFilters = () => {
    setDocumentFilters({
      code: "",
      status: "",
      dateFrom: "",
      dateTo: "",
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "UPCOMING":
        return "primary";
      case "COMPLETED":
        return "success";
      case "MISSED":
        return "error";
      default:
        return "default";
    }
  };

  const getRenewalStatusColor = (status) => {
    switch (status) {
      case "EXPIRED":
        return "error";
      case "CRITICAL":
        return "error";
      case "WARNING":
        return "warning";
      case "OK":
        return "success";
      case "NO_DATE":
        return "default";
      default:
        return "default";
    }
  };

  // Filter reminders based on role and filter criteria, then sort
  const filteredReminders = reminders
    .filter((reminder) => {
      // Role-based visibility: non-head roles only see reminders they created or are assigned to
      if (!isHeadRole) {
        const isCreator = reminder.created_by === user_id;
        const isAssignee = reminder.assignee_id === user_id;
        if (!isCreator && !isAssignee) return false;
      }

      const matchesAssignee =
        !filters.assigneeName ||
        (reminder.assignee?.name &&
          reminder.assignee.name
            .toLowerCase()
            .includes(filters.assigneeName.toLowerCase()));
      const matchesTitle =
        !filters.titleSearch ||
        reminder.title
          .toLowerCase()
          .includes(filters.titleSearch.toLowerCase());
      const matchesStatus =
        !filters.status || reminder.status === filters.status;

      const reminderDate = new Date(reminder.reminder_datetime);
      const matchesDateFrom =
        !filters.dateFrom || reminderDate >= new Date(filters.dateFrom);
      const matchesDateTo =
        !filters.dateTo || reminderDate <= new Date(filters.dateTo);

      return (
        matchesAssignee &&
        matchesTitle &&
        matchesStatus &&
        matchesDateFrom &&
        matchesDateTo
      );
    })
    .sort((a, b) => {
      // Status priority: UPCOMING → MISSED → COMPLETED
      const statusOrder = { UPCOMING: 0, MISSED: 1, COMPLETED: 2 };
      const statusDiff =
        (statusOrder[a.status] ?? 1) - (statusOrder[b.status] ?? 1);
      if (statusDiff !== 0) return statusDiff;

      const dateA = new Date(a.reminder_datetime);
      const dateB = new Date(b.reminder_datetime);

      // UPCOMING: soonest date first (ascending — most imminent on top)
      if (a.status === "UPCOMING") return dateA - dateB;

      // MISSED & COMPLETED: most recent first (descending)
      return dateB - dateA;
    });

  // Filter documents based on filter criteria
  const filteredDocuments = documents.filter((doc) => {
    const matchesCode =
      !documentFilters.code ||
      (doc.owner?.owner_code &&
        doc.owner.owner_code
          .toLowerCase()
          .includes(documentFilters.code.toLowerCase()));

    const matchesStatus =
      !documentFilters.status ||
      doc.doc_renewal_status === documentFilters.status;

    if (doc.doc_renew_date) {
      const renewDate = new Date(doc.doc_renew_date);
      const matchesDateFrom =
        !documentFilters.dateFrom ||
        renewDate >= new Date(documentFilters.dateFrom);
      const matchesDateTo =
        !documentFilters.dateTo ||
        renewDate <= new Date(documentFilters.dateTo);

      return matchesCode && matchesStatus && matchesDateFrom && matchesDateTo;
    }

    return matchesCode && matchesStatus;
  });

  const exportRemindersToExcel = () => {
    const rows = filteredReminders.map((reminder) => ({
      Type: reminder.type || "-",
      Title: reminder.title || "-",
      Description: reminder.description || "-",
      "Reminder Date": formatDate(reminder.reminder_datetime),
      Status: reminder.status || "-",
      "Created By": reminder.creator?.name || "-",
      "Created On": formatDate(reminder.created_at),
      Assignee: reminder.assignee?.name || "-",
    }));

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Reminders");
    XLSX.writeFile(
      workbook,
      `Reminders_${new Date().toISOString().slice(0, 10)}.xlsx`,
    );
  };

  const exportDocumentsToExcel = () => {
    const rows = filteredDocuments.map((doc) => ({
      Type: doc.doc_master_type || "-",
      Code: doc.owner?.owner_code || "-",
      "Doc Name": doc.doc_name || "-",
      "Doc No.": doc.doc_number || "-",
      "Renew Date": formatDate(doc.doc_renew_date),
      "Days Remaining":
        doc.doc_days_remaining === null
          ? "-"
          : doc.doc_days_remaining < 0
          ? `Expired ${Math.abs(doc.doc_days_remaining)} days ago`
          : `${doc.doc_days_remaining} days`,
      Status: doc.doc_renewal_status || "-",
    }));

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Documents");
    XLSX.writeFile(
      workbook,
      `Documents_${new Date().toISOString().slice(0, 10)}.xlsx`,
    );
  };

  return (
    <Box sx={{ p: 3, bgcolor: "#f5f5f5", height: "100vh", overflow: "hidden" }}>
      <Typography
        variant="h4"
        sx={{ mb: 3, fontWeight: 600, color: "#1976d2" }}
      >
        Reminder Master
      </Typography>

      <Grid container spacing={3} sx={{ height: "calc(100vh - 100px)" }}>
        {/* Left Section - Create/Update Reminder Form - FIXED */}
        <Grid
          item
          xs={12}
          md={5}
          lg={3.2}
          sx={{ height: "100%", overflow: "hidden" }}
        >
          <Card
            sx={{
              boxShadow: 3,
              height: "100%",
              overflowY: "auto",
              "&::-webkit-scrollbar": {
                display: "none",
              },
              msOverflowStyle: "none",
              scrollbarWidth: "none",
            }}
          >
            <CardContent>
              <Typography
                variant="h6"
                sx={{ mb: 3, fontWeight: 500, color: "#1976d2" }}
              >
                {editMode ? "Update Reminder" : "Create New Reminder"}
              </Typography>

              <Divider sx={{ mb: 3 }} />

              <Stack spacing={2.5}>
                <TextField
                  fullWidth
                  required
                  label="Type"
                  name="type"
                  value={formData.type}
                  onChange={handleInputChange}
                  variant="outlined"
                  size="small"
                  placeholder="e.g., DOCUMENT, USER, GENERAL"
                  helperText="Common types: DOCUMENT, USER, GENERAL"
                />

                <TextField
                  fullWidth
                  required
                  label="Title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  variant="outlined"
                  size="small"
                />

                <TextField
                  fullWidth
                  label="Description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  variant="outlined"
                  multiline
                  rows={3}
                  size="small"
                />

                <TextField
                  fullWidth
                  required
                  label="Reminder Date"
                  name="reminder_datetime"
                  type="date"
                  value={formData.reminder_datetime.slice(0, 10)}
                  onChange={handleInputChange}
                  variant="outlined"
                  size="small"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  inputProps={{
                    min: new Date().toISOString().split("T")[0],
                  }}
                />

                {isHeadRole && (
                  <Autocomplete
                    options={allStaff || []}
                    getOptionLabel={(option) => option.name || ""}
                    value={
                      allStaff?.find(
                        (user) => user.id === formData.assignee_id,
                      ) || null
                    }
                    onChange={handleUserChange}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Assign To User (Optional)"
                        size="small"
                        variant="outlined"
                        helperText="For USER type reminders"
                      />
                    )}
                  />
                )}

                <Divider sx={{ my: 1 }} />

                <Stack spacing={1.5}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    size="large"
                    startIcon={editMode ? <EditIcon /> : <AddIcon />}
                    onClick={handleSaveReminder}
                    sx={{ py: 1.2 }}
                    disabled={loading}
                  >
                    {loading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : editMode ? (
                      "Update Reminder"
                    ) : (
                      "Save Reminder"
                    )}
                  </Button>
                  <Button
                    fullWidth
                    variant="outlined"
                    color="secondary"
                    size="medium"
                    startIcon={<ClearIcon />}
                    onClick={handleClearForm}
                    disabled={loading}
                  >
                    Clear Form
                  </Button>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Section - Tabs for Reminders and Documents - SCROLLABLE */}
        <Grid
          item
          xs={12}
          md={7}
          lg={8.8}
          sx={{
            height: "100%",
            overflowY: "auto",
            "&::-webkit-scrollbar": {
              display: "none",
            },
            msOverflowStyle: "none",
            scrollbarWidth: "none",
          }}
        >
          {isHeadRole && (
            <Card sx={{ boxShadow: 3, mb: 2 }}>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                sx={{
                  borderBottom: 1,
                  borderColor: "divider",
                  px: 2,
                }}
              >
                <Tab
                  label="Reminders"
                  icon={<ListAltIcon />}
                  iconPosition="start"
                />
                <Tab
                  label="Documents"
                  icon={<DescriptionIcon />}
                  iconPosition="start"
                />
              </Tabs>
            </Card>
          )}

          {/* Reminders Tab */}
          {tabValue === 0 && (
            <>
              <Card sx={{ boxShadow: 3, mb: 2 }}>
                <CardContent sx={{ pb: 2 }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ mb: 2 }}
                  >
                    <FilterListIcon color="primary" />
                    <Typography variant="h6" sx={{ fontWeight: 500 }}>
                      Filters
                    </Typography>
                  </Stack>

                  <Stack
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    flexWrap="wrap"
                    useFlexGap
                  >
                    {isHeadRole && (
                      <TextField
                        label="Assignee Name"
                        name="assigneeName"
                        value={filters.assigneeName}
                        onChange={handleFilterChange}
                        variant="outlined"
                        size="small"
                        sx={{ minWidth: 160, flex: 1 }}
                        placeholder="Search by assignee"
                      />
                    )}

                    <TextField
                      label="Search Title"
                      name="titleSearch"
                      value={filters.titleSearch}
                      onChange={handleFilterChange}
                      variant="outlined"
                      size="small"
                      sx={{ minWidth: 160, flex: 1 }}
                      InputProps={{
                        startAdornment: (
                          <SearchIcon sx={{ mr: 1, color: "action.active" }} />
                        ),
                      }}
                    />

                    <TextField
                      select
                      label="Status"
                      name="status"
                      value={filters.status}
                      onChange={handleFilterChange}
                      variant="outlined"
                      size="small"
                      sx={{ minWidth: 140, flex: 1 }}
                    >
                      <MenuItem value="">All Status</MenuItem>
                      <MenuItem value="UPCOMING">UPCOMING</MenuItem>
                      <MenuItem value="COMPLETED">COMPLETED</MenuItem>
                      <MenuItem value="MISSED">MISSED</MenuItem>
                    </TextField>

                    <TextField
                      label="Date From"
                      name="dateFrom"
                      type="date"
                      value={filters.dateFrom}
                      onChange={handleFilterChange}
                      variant="outlined"
                      size="small"
                      sx={{ minWidth: 150, flex: 1 }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />

                    <TextField
                      label="Date To"
                      name="dateTo"
                      type="date"
                      value={filters.dateTo}
                      onChange={handleFilterChange}
                      variant="outlined"
                      size="small"
                      sx={{ minWidth: 150, flex: 1 }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />

                    <Button
                      variant="outlined"
                      color="secondary"
                      size="medium"
                      startIcon={<ClearIcon />}
                      onClick={handleClearFilters}
                      sx={{ height: "40px", minWidth: 130 }}
                    >
                      Clear
                    </Button>

                    <Tooltip title="Export to Excel">
                      <IconButton
                        onClick={exportRemindersToExcel}
                        sx={{ height: "40px", width: "40px" }}
                        disabled={filteredReminders.length === 0}
                      >
                        <img
                          src={ExcelIcon}
                          alt="Export Excel"
                          style={{
                            width: 26,
                            height: 26,
                            opacity: filteredReminders.length === 0 ? 0.4 : 1,
                          }}
                        />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </CardContent>
              </Card>

              <Card sx={{ boxShadow: 3, height: "100%" }}>
                <CardContent>
                  {loading ? (
                    <Box
                      sx={{ display: "flex", justifyContent: "center", py: 4 }}
                    >
                      <CircularProgress />
                    </Box>
                  ) : (
                    <TableContainer component={Paper}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell
                              sx={{
                                fontWeight: 600,
                                bgcolor: "#1976d2",
                                color: "white",
                              }}
                            >
                              Type
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 600,
                                bgcolor: "#1976d2",
                                color: "white",
                              }}
                            >
                              Title
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 600,
                                bgcolor: "#1976d2",
                                color: "white",
                              }}
                            >
                              Description
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 600,
                                bgcolor: "#1976d2",
                                color: "white",
                              }}
                            >
                              Reminder Date
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 600,
                                bgcolor: "#1976d2",
                                color: "white",
                              }}
                            >
                              Status
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 600,
                                bgcolor: "#1976d2",
                                color: "white",
                              }}
                            >
                              Created By
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 600,
                                bgcolor: "#1976d2",
                                color: "white",
                              }}
                            >
                              Created On
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 600,
                                bgcolor: "#1976d2",
                                color: "white",
                              }}
                            >
                              Assignee
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                fontWeight: 600,
                                bgcolor: "#1976d2",
                                color: "white",
                              }}
                            >
                              Actions
                            </TableCell>
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          {filteredReminders.length === 0 ? (
                            <TableRow>
                              <TableCell
                                colSpan={9}
                                align="center"
                                sx={{ py: 4 }}
                              >
                                <Typography
                                  variant="body1"
                                  color="textSecondary"
                                >
                                  No reminders found matching the filters.
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ) : (
                            filteredReminders.map((reminder) => (
                              <TableRow
                                key={reminder.reminder_id}
                                sx={{
                                  "&:hover": { bgcolor: "#f5f5f5" },
                                  transition: "background-color 0.2s",
                                }}
                              >
                                <TableCell>
                                  <Chip
                                    label={reminder.type}
                                    size="small"
                                    variant="outlined"
                                  />
                                </TableCell>
                                <TableCell sx={{ fontWeight: 500 }}>
                                  {reminder.title}
                                </TableCell>
                                <TableCell sx={{ maxWidth: 200 }}>
                                  {reminder.description || "-"}
                                </TableCell>
                                <TableCell sx={{ fontSize: "0.875rem" }}>
                                  {formatDate(reminder.reminder_datetime)}
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={reminder.status}
                                    color={getStatusColor(reminder.status)}
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell>
                                  {reminder.creator?.name || "-"}
                                </TableCell>
                                <TableCell>
                                  {formatDate(reminder.created_at)}
                                </TableCell>
                                <TableCell>
                                  {reminder.assignee?.name || "-"}
                                </TableCell>
                                <TableCell align="center">
                                  <Stack
                                    direction="row"
                                    spacing={1}
                                    justifyContent="center"
                                  >
                                    <Tooltip title="Edit">
                                      <IconButton
                                        color="primary"
                                        size="small"
                                        onClick={() =>
                                          handleEditReminder(reminder)
                                        }
                                      >
                                        <EditIcon />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Delete">
                                      <IconButton
                                        color="error"
                                        size="small"
                                        onClick={() =>
                                          handleDeleteClick(reminder)
                                        }
                                      >
                                        <DeleteIcon />
                                      </IconButton>
                                    </Tooltip>
                                  </Stack>
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </CardContent>
              </Card>
            </>
          )}

          {/* Documents Tab */}
          {tabValue === 1 && (
            <>
              {/* Compact Stats Cards */}
              {documentStats && (
                <Grid container spacing={1.5} sx={{ mb: 2 }}>
                  <Grid item xs={6} sm={4} md={2.4}>
                    <Card sx={{ bgcolor: "#e3f2fd" }}>
                      <CardContent sx={{ textAlign: "center", py: 1, px: 1 }}>
                        <Typography variant="h5" color="primary">
                          {documentStats.total}
                        </Typography>
                        <Typography variant="caption">Total</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6} sm={4} md={2.4}>
                    <Card sx={{ bgcolor: "#ffebee" }}>
                      <CardContent sx={{ textAlign: "center", py: 1, px: 1 }}>
                        <Typography variant="h5" color="error">
                          {documentStats.expired}
                        </Typography>
                        <Typography variant="caption">Expired</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6} sm={4} md={2.4}>
                    <Card sx={{ bgcolor: "#fff3e0" }}>
                      <CardContent sx={{ textAlign: "center", py: 1, px: 1 }}>
                        <Typography variant="h5" color="error">
                          {documentStats.critical}
                        </Typography>
                        <Typography variant="caption">Critical</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6} sm={4} md={2.4}>
                    <Card sx={{ bgcolor: "#fff9c4" }}>
                      <CardContent sx={{ textAlign: "center", py: 1, px: 1 }}>
                        <Typography variant="h5" color="warning.dark">
                          {documentStats.warning}
                        </Typography>
                        <Typography variant="caption">Warning</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6} sm={4} md={2.4}>
                    <Card sx={{ bgcolor: "#e8f5e9" }}>
                      <CardContent sx={{ textAlign: "center", py: 1, px: 1 }}>
                        <Typography variant="h5" color="success.main">
                          {documentStats.ok}
                        </Typography>
                        <Typography variant="caption">OK</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              )}

              {/* Document Filters */}
              <Card sx={{ boxShadow: 3, mb: 2 }}>
                <CardContent sx={{ pb: 2 }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ mb: 2 }}
                  >
                    <FilterListIcon color="primary" />
                    <Typography variant="h6" sx={{ fontWeight: 500 }}>
                      Filters
                    </Typography>
                  </Stack>

                  <Stack
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    flexWrap="wrap"
                    useFlexGap
                  >
                    <TextField
                      label="Search Code"
                      name="code"
                      value={documentFilters.code}
                      onChange={handleDocumentFilterChange}
                      variant="outlined"
                      size="small"
                      sx={{ minWidth: 140, flex: 1 }}
                      placeholder="Search by code"
                      InputProps={{
                        startAdornment: (
                          <SearchIcon sx={{ mr: 1, color: "action.active" }} />
                        ),
                      }}
                    />

                    <TextField
                      select
                      label="Status"
                      name="status"
                      value={documentFilters.status}
                      onChange={handleDocumentFilterChange}
                      variant="outlined"
                      size="small"
                      sx={{ minWidth: 140, flex: 1 }}
                    >
                      <MenuItem value="">All Status</MenuItem>
                      <MenuItem value="OK">OK</MenuItem>
                      <MenuItem value="WARNING">WARNING</MenuItem>
                      <MenuItem value="CRITICAL">CRITICAL</MenuItem>
                      <MenuItem value="EXPIRED">EXPIRED</MenuItem>
                      <MenuItem value="NO_DATE">NO DATE</MenuItem>
                    </TextField>

                    <TextField
                      label="Date From"
                      name="dateFrom"
                      type="date"
                      value={documentFilters.dateFrom}
                      onChange={handleDocumentFilterChange}
                      variant="outlined"
                      size="small"
                      sx={{ minWidth: 150, flex: 1 }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />

                    <TextField
                      label="Date To"
                      name="dateTo"
                      type="date"
                      value={documentFilters.dateTo}
                      onChange={handleDocumentFilterChange}
                      variant="outlined"
                      size="small"
                      sx={{ minWidth: 150, flex: 1 }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />

                    <Button
                      variant="outlined"
                      color="secondary"
                      size="medium"
                      startIcon={<ClearIcon />}
                      onClick={handleClearDocumentFilters}
                      sx={{ height: "40px", minWidth: 130 }}
                    >
                      Clear
                    </Button>

                    <Tooltip title="Export to Excel">
                      <IconButton
                        onClick={exportDocumentsToExcel}
                        sx={{ height: "40px", width: "40px" }}
                        disabled={filteredDocuments.length === 0}
                      >
                        <img
                          src={ExcelIcon}
                          alt="Export Excel"
                          style={{
                            width: 26,
                            height: 26,
                            opacity: filteredDocuments.length === 0 ? 0.4 : 1,
                          }}
                        />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </CardContent>
              </Card>

              <Card sx={{ boxShadow: 3, height: "100%" }}>
                <CardContent>
                  {loading ? (
                    <Box
                      sx={{ display: "flex", justifyContent: "center", py: 4 }}
                    >
                      <CircularProgress />
                    </Box>
                  ) : (
                    <TableContainer component={Paper}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell
                              sx={{
                                fontWeight: 600,
                                bgcolor: "#1976d2",
                                color: "white",
                              }}
                            >
                              Type
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 600,
                                bgcolor: "#1976d2",
                                color: "white",
                              }}
                            >
                              Code
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 600,
                                bgcolor: "#1976d2",
                                color: "white",
                              }}
                            >
                              Doc Name
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 600,
                                bgcolor: "#1976d2",
                                color: "white",
                              }}
                            >
                              Doc No.
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 600,
                                bgcolor: "#1976d2",
                                color: "white",
                              }}
                            >
                              Renew Date
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 600,
                                bgcolor: "#1976d2",
                                color: "white",
                              }}
                            >
                              Days Remaining
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 600,
                                bgcolor: "#1976d2",
                                color: "white",
                              }}
                            >
                              Status
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                fontWeight: 600,
                                bgcolor: "#1976d2",
                                color: "white",
                              }}
                            >
                              Actions
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {filteredDocuments.length === 0 ? (
                            <TableRow>
                              <TableCell
                                colSpan={8}
                                align="center"
                                sx={{ py: 4 }}
                              >
                                <Typography
                                  variant="body1"
                                  color="textSecondary"
                                >
                                  No documents found matching the filters.
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ) : (
                            filteredDocuments.map((doc) => (
                              <TableRow
                                key={doc.doc_id}
                                sx={{
                                  "&:hover": { bgcolor: "#f5f5f5" },
                                  transition: "background-color 0.2s",
                                }}
                              >
                                <TableCell>
                                  <Chip
                                    label={doc.doc_master_type}
                                    size="small"
                                    variant="outlined"
                                  />
                                </TableCell>
                                <TableCell sx={{ fontWeight: 500 }}>
                                  {doc.owner?.owner_code || "-"}
                                </TableCell>
                                <TableCell>{doc.doc_name}</TableCell>
                                <TableCell>{doc.doc_number}</TableCell>
                                <TableCell>
                                  {formatDate(doc.doc_renew_date)}
                                </TableCell>
                                <TableCell>
                                  <Typography
                                    color={
                                      doc.doc_days_remaining === null
                                        ? "text.secondary"
                                        : doc.doc_days_remaining < 0
                                        ? "error"
                                        : doc.doc_days_remaining <= 7
                                        ? "warning.main"
                                        : "success.main"
                                    }
                                    fontWeight={500}
                                  >
                                    {doc.doc_days_remaining !== null
                                      ? `${doc.doc_days_remaining} days`
                                      : "-"}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={doc.doc_renewal_status}
                                    color={getRenewalStatusColor(
                                      doc.doc_renewal_status,
                                    )}
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell align="center">
                                  <Stack
                                    direction="row"
                                    spacing={0.5}
                                    justifyContent="center"
                                  >
                                    {/* View document file */}
                                    <Tooltip title="View Document">
                                      <IconButton
                                        size="small"
                                        onClick={() =>
                                          setViewerDoc({
                                            name: doc.doc_name,
                                            number: doc.doc_number,
                                            file: doc.doc_file || null,
                                            fileName: doc.doc_file_name || null,
                                            renewalDate:
                                              doc.doc_renew_date || null,
                                          })
                                        }
                                        sx={{ color: "#7B6ED6" }}
                                      >
                                        <VisibilityRoundedIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    {/* Navigate to master page to edit */}
                                    {(doc.doc_master_type === "V" ||
                                      doc.doc_master_type === "D") && (
                                      <Tooltip
                                        title={
                                          doc.doc_master_type === "V"
                                            ? "Edit in Vehicle Master"
                                            : "Edit in Driver Master"
                                        }
                                      >
                                        <IconButton
                                          size="small"
                                          color="primary"
                                          onClick={() =>
                                            handleNavigateToMaster(doc)
                                          }
                                        >
                                          <EditIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                  </Stack>
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </Grid>
      </Grid>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCancelDelete}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the reminder "
            {reminderToDelete?.title}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Document Viewer Dialog */}
      <DocumentViewerDialog
        doc={viewerDoc}
        onClose={() => setViewerDoc(null)}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ReminderMaster;
